import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useConvexAuth, useMutation } from "convex/react";
import { Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "#/../convex/_generated/api";
import type { Doc } from "#/../convex/_generated/dataModel";
import { BulkActionBar } from "#/components/listings/BulkActionBar";
import { MyListingCard } from "#/components/listings/MyListingCard";
import { Button } from "#/components/ui/button";
import { Checkbox } from "#/components/ui/checkbox";
import { useBulkSelection } from "#/hooks/use-bulk-selection";
import { useSignInModal } from "#/hooks/useSignInModal";

export const Route = createFileRoute("/my-listings")({
	component: MyListingsPage,
});

function MyListingsPage() {
	const { isLoading, isAuthenticated } = useConvexAuth();
	const { open } = useSignInModal();
	useEffect(() => {
		if (!isLoading && !isAuthenticated) open();
	}, [isLoading, isAuthenticated, open]);
	if (isLoading) return null;
	if (!isAuthenticated) return null;
	return <MyListingsContent />;
}

function MyListingsContent() {
	const { t } = useTranslation("myListings");
	const { data: listings = [] } = useQuery(
		convexQuery(api.listings.listBySeller, {}),
	);
	const deactivate = useMutation(api.listings.deactivateListing);
	const markAsSold = useMutation(api.listings.markAsSold);
	const renew = useMutation(api.listings.renewListing);
	const bulkDeactivate = useMutation(api.listings.bulkDeactivate);
	const bulkMarkAsSold = useMutation(api.listings.bulkMarkAsSold);
	const bulkReactivate = useMutation(api.listings.bulkReactivate);

	const [pending, setPending] = useState<string | null>(null);
	const [isBulkPending, setIsBulkPending] = useState(false);

	const activeSelection = useBulkSelection();
	const inactiveSelection = useBulkSelection();

	const active = listings.filter((l) => l.isActive);
	const inactive = listings.filter((l) => !l.isActive);

	const activeIds = active.map((l) => l._id);
	const inactiveIds = inactive.map((l) => l._id);

	async function handleDeactivate(id: string) {
		setPending(id);
		try {
			await deactivate({ listingId: id as Doc<"listings">["_id"] });
		} finally {
			setPending(null);
		}
	}

	async function handleMarkAsSold(id: string) {
		setPending(id);
		try {
			await markAsSold({ listingId: id as Doc<"listings">["_id"] });
		} finally {
			setPending(null);
		}
	}

	async function handleRenew(id: string) {
		setPending(id);
		try {
			await renew({ listingId: id as Doc<"listings">["_id"] });
		} finally {
			setPending(null);
		}
	}

	async function handleBulkDeactivate() {
		setIsBulkPending(true);
		try {
			await bulkDeactivate({
				listingIds: [
					...activeSelection.selectedIds,
				] as Doc<"listings">["_id"][],
			});
			activeSelection.exitSelectMode();
		} finally {
			setIsBulkPending(false);
		}
	}

	async function handleBulkMarkAsSold() {
		setIsBulkPending(true);
		try {
			await bulkMarkAsSold({
				listingIds: [
					...activeSelection.selectedIds,
				] as Doc<"listings">["_id"][],
			});
			activeSelection.exitSelectMode();
		} finally {
			setIsBulkPending(false);
		}
	}

	async function handleBulkReactivate() {
		setIsBulkPending(true);
		try {
			await bulkReactivate({
				listingIds: [
					...inactiveSelection.selectedIds,
				] as Doc<"listings">["_id"][],
			});
			inactiveSelection.exitSelectMode();
		} finally {
			setIsBulkPending(false);
		}
	}

	const allActiveSelected =
		activeIds.length > 0 &&
		activeIds.every((id) => activeSelection.selectedIds.has(id));

	const allInactiveSelected =
		inactiveIds.length > 0 &&
		inactiveIds.every((id) => inactiveSelection.selectedIds.has(id));

	return (
		<div className="page-wrap py-12">
			<div className="mx-auto max-w-3xl flex flex-col gap-10">
				<div className="flex items-end justify-between gap-4">
					<div>
						<p className="sec-tag">{t("header.tag")}</p>
						<h1 className="display-title text-[clamp(36px,4vw,52px)] leading-none text-foreground">
							{t("header.title")}
						</h1>
					</div>
					<Button asChild>
						<Link to="/sell">
							<Plus className="size-4" />
							{t("header.listDisc")}
						</Link>
					</Button>
				</div>

				{listings.length === 0 && (
					<div className="rounded-[var(--radius)] border border-dashed border-border py-24 flex flex-col items-center text-center">
						<div className="relative mb-5 flex size-16 items-center justify-center rounded-full bg-muted">
							<div className="relative h-8 w-8 rounded-full border-[6px] border-muted-foreground/35">
								<div className="absolute inset-0 m-auto h-2 w-2 rounded-full bg-muted-foreground/35" />
							</div>
						</div>
						<p className="font-semibold text-foreground">{t("empty.title")}</p>
						<p className="mt-1 text-sm text-muted-foreground max-w-xs">
							{t("empty.description")}
						</p>
						<Button className="mt-6" asChild>
							<Link to="/sell">{t("header.listDisc")}</Link>
						</Button>
					</div>
				)}

				{active.length > 0 && (
					<section>
						<div className="mb-4 flex items-center gap-2">
							<span className="inline-block h-2 w-2 rounded-full bg-accent" />
							<span className="text-sm font-semibold text-foreground">
								{t("sections.active")} · {active.length}
							</span>
							<div className="ml-auto flex items-center gap-2">
								{activeSelection.isSelectMode ? (
									<>
										<div className="flex items-center gap-2">
											<Checkbox
												id="select-all-active"
												checked={allActiveSelected}
												onCheckedChange={() =>
													activeSelection.selectAll(activeIds)
												}
												aria-label="Select all active listings"
											/>
											<label
												htmlFor="select-all-active"
												className="text-sm text-muted-foreground cursor-pointer select-none"
											>
												{t("selectAll")}
											</label>
										</div>
										<Button
											variant="ghost"
											size="sm"
											onClick={activeSelection.exitSelectMode}
										>
											{t("cancel")}
										</Button>
									</>
								) : (
									<Button
										variant="ghost"
										size="sm"
										onClick={activeSelection.enterSelectMode}
									>
										{t("select")}
									</Button>
								)}
							</div>
						</div>
						<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
							{active.map((listing) => (
								<MyListingCard
									key={listing._id}
									listing={listing}
									isPending={pending === listing._id}
									isSelectMode={activeSelection.isSelectMode}
									isSelected={activeSelection.selectedIds.has(listing._id)}
									onToggleSelect={activeSelection.toggleSelect}
									action={
										<>
											<Button
												variant="outline"
												size="sm"
												disabled={pending === listing._id}
												onClick={() => handleMarkAsSold(listing._id)}
											>
												{t("actions.markAsSold")}
											</Button>
											<Button
												variant="ghost"
												size="sm"
												disabled={pending === listing._id}
												onClick={() => handleDeactivate(listing._id)}
											>
												{t("actions.deactivate")}
											</Button>
										</>
									}
								/>
							))}
						</div>
						{activeSelection.selectedIds.size >= 1 && (
							<BulkActionBar
								selectedCount={activeSelection.selectedIds.size}
								onDeactivate={handleBulkDeactivate}
								onMarkAsSold={handleBulkMarkAsSold}
								isPending={isBulkPending}
							/>
						)}
					</section>
				)}

				{inactive.length > 0 && (
					<section>
						<div className="mb-4 flex items-center gap-2">
							<span className="inline-block h-2 w-2 rounded-full bg-muted-foreground/35" />
							<span className="text-sm font-semibold text-muted-foreground">
								{t("sections.inactive")} · {inactive.length}
							</span>
							<div className="ml-auto flex items-center gap-2">
								{inactiveSelection.isSelectMode ? (
									<>
										<div className="flex items-center gap-2">
											<Checkbox
												id="select-all-inactive"
												checked={allInactiveSelected}
												onCheckedChange={() =>
													inactiveSelection.selectAll(inactiveIds)
												}
												aria-label="Select all inactive listings"
											/>
											<label
												htmlFor="select-all-inactive"
												className="text-sm text-muted-foreground cursor-pointer select-none"
											>
												{t("selectAll")}
											</label>
										</div>
										<Button
											variant="ghost"
											size="sm"
											onClick={inactiveSelection.exitSelectMode}
										>
											{t("cancel")}
										</Button>
									</>
								) : (
									<Button
										variant="ghost"
										size="sm"
										onClick={inactiveSelection.enterSelectMode}
									>
										{t("select")}
									</Button>
								)}
							</div>
						</div>
						<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
							{inactive.map((listing) => (
								<MyListingCard
									key={listing._id}
									listing={listing}
									isPending={pending === listing._id}
									inactive
									isSelectMode={inactiveSelection.isSelectMode}
									isSelected={inactiveSelection.selectedIds.has(listing._id)}
									onToggleSelect={inactiveSelection.toggleSelect}
									action={
										<Button
											size="sm"
											disabled={pending === listing._id}
											onClick={() => handleRenew(listing._id)}
										>
											{t("actions.reactivate")}
										</Button>
									}
								/>
							))}
						</div>
						{inactiveSelection.selectedIds.size >= 1 && (
							<BulkActionBar
								variant="inactive"
								selectedCount={inactiveSelection.selectedIds.size}
								onReactivate={handleBulkReactivate}
								isPending={isBulkPending}
							/>
						)}
					</section>
				)}
			</div>
		</div>
	);
}
